import { nortadaLogger } from '../../logger';
import { formatSize } from '../../../../constants/helpers/units';
import { capitalize } from '../../../../constants/helpers/utils';
import { humanizeDistanceBetweenDates } from '../../../../constants/helpers/dates';

import {
  HandleDownloadReport,
  HandleOpenCancelDialog,
  HandleOpenDeleteDialog,
  HandleOpenDetailsDialog,
  HandleOpenRegenerateDialog,
  HandleOpenRetryDialog
} from '../actions/interfaces';
import Actions from '../actions';
import RetryAction from '../actions/retry/tableActionTrigger.tsx';
import CancelAction from '../actions/cancel/tableActionTrigger';
import DeleteAction from '../actions/delete/tableActionTrigger';
import DetailsAction from '../actions/details/detailsAction';
import DownloadAction from '../actions/download/downloadAction';
import RegenerateAction from '../actions/regenerate/tableActionTrigger';
import { Action, Report, STATUS, TableData, ReportsValidationStatus } from '../interfaces';

import { CanceledReport } from '../actions/cancel/interfaces';
import { DeletedReport } from '../actions/delete/interfaces';
import { RetriedReport } from '../actions/retry/interfaces';
import { RegeneratedReport } from '../actions/regenerate/interfaces';

export const getPermissionToGenerateReports = (status?: ReportsValidationStatus): boolean => {
  switch (status) {
    case 'ALLOWED':
      return true;
    case 'PENDING_REPORTS_LIMIT_REACHED':
    case 'REPORTS_LIMIT_REACHED':
    case undefined:
      return false;
    default:
      nortadaLogger.critical(`${status} is not a valid report generate status.`);
      return false;
  }
};

export const mapReportToTableStructure = (
  item: Report,
  generationAllowed: boolean = false,
  handleOpenDeleteDialog: HandleOpenDeleteDialog,
  handleOpenCancelDialog: HandleOpenCancelDialog,
  handleOpenDetailsDialog: HandleOpenDetailsDialog,
  handleOpenRegenerateDialog: HandleOpenRegenerateDialog,
  handleOpenRetryDialog: HandleOpenRetryDialog,
  handleDownloadReport: HandleDownloadReport
): TableData => {
  const { status, id, name } = item;
  const getActions = (availableActions: Action[]) => (
    <Actions
      Download={
        availableActions.includes('download') && (
          <DownloadAction item={item} handleDownloadReport={handleDownloadReport} />
        )
      }
      Details={
        availableActions.includes('details') && (
          <DetailsAction item={item} handleOpenDetailsDialog={handleOpenDetailsDialog} />
        )
      }
      Delete={
        availableActions.includes('delete') && (
          <DeleteAction item={item as DeletedReport} handleOpenDeleteDialog={handleOpenDeleteDialog} />
        )
      }
      Cancel={
        availableActions.includes('cancel') && (
          <CancelAction item={item as CanceledReport} handleOpenCancelDialog={handleOpenCancelDialog} />
        )
      }
    />
  );

  const removeGenerationWhenForbidden = (availableActions: Action[], allowed: boolean) => {
    if (allowed) {
      return availableActions;
    }

    return availableActions.filter((current: Action) => current !== 'regenerate' && current !== 'retry');
  };

  switch (item.status) {
    case STATUS.CREATED: {
      return {
        id,
        name,
        status,
        size: formatSize(item.reportFileSize),
        expireDate: capitalize(humanizeDistanceBetweenDates(item.expireDate)),
        actions: getActions(
          removeGenerationWhenForbidden(['download', 'details', 'delete'], generationAllowed)
        )
      };
    }
    case STATUS.PENDING: {
      return {
        id,
        name,
        status,
        size: '-',
        expireDate: '-',
        progress: item.progress,
        actions: getActions(['details', 'cancel'])
      };
    }
    case STATUS.FAILED: {
      return {
        id,
        name,
        status,
        size: '-',
        expireDate: '-',
        actions: getActions(removeGenerationWhenForbidden(['details', 'delete'], generationAllowed))
      };
    }
    case STATUS.EXPIRED: {
      return {
        id,
        name,
        status,
        size: formatSize(item.reportFileSize),
        expireDate: capitalize(humanizeDistanceBetweenDates(item.expireDate)),
        actions: getActions(removeGenerationWhenForbidden(['details', 'delete'], generationAllowed))
      };
    }
    default: {
      throw nortadaLogger.critical(`${status} is not a valid report type`);
    }
  }
};
